// Module `ogsm`: one-page plans (Objective, Goals, Strategies, Measures) for
// the organisation, every unit and every collaboration, with KPIs that add up
// along the "contributes to" chain (docs/superpowers/plans/2026-10-11-ogsm-kpis.md).
//
// Statuses, expected values and effort are calculated by the API
// (apps/api/src/modules/ogsm/lib/status.ts), never typed.
//
// Quarters: { year, quarter } as in the workload round ("Q3 2026").
// Numbers: KPI values, baselines and targets are plain numbers with at most
// two decimals (numeric(14,2)). Money in effort figures: integer euro cents.
// Visibility: every member of the org can read every OGSM (its words,
// structure and statuses). Some figures are not for everyone (plan "Who sees
// which figures"):
//   - money: euro amounts are for org owners and admins only (as in the
//     collab module). For everyone else the euro fields are null: effort
//     cents, portfolio cents and budget cents, and for money KPIs (source
//     'collab.cost', or a 'children' KPI that adds up a money KPI) the value,
//     expected value, gap, baseline, target and contribution shares.
//     Percentages (cost against budget) and days stay.
//   - workload: the values of the workload sources (WORKLOAD_SOURCES) are for
//     owners/admins and the managers of that unit or a unit above it; a
//     'children' KPI that adds one up follows the same rule. Everyone else
//     gets null (the status stays).
//   - small groups: workload sources, a child's weightDays and a unit's own
//     effort days are null when fewer than 5 submitted statements are behind
//     the figure, for everyone.
// KpiValue.hidden says there is a value, but not for this caller.
// Days in effort figures: days in the quarter (days per week × weeks per quarter).
//
// "canEdit" says whether the caller may change an OGSM: owner/admin
// everything; a unit's manager that unit's OGSM and those of the units below;
// a collab's effective owner its own OGSM. The same people enter that OGSM's
// manual readings.
//
// Default quarter (no ?quarter=): the current quarter, unless its workload
// round is still open; then the latest quarter of the year whose round is
// closed. The responses say which quarter they are for.
import { z } from "zod";
import { uuid } from "../schemas/common";
import { isoDate, text, textOrEmpty, type Id } from "./common";

// ---------- building blocks ----------

export const OGSM_SCOPES = ["org", "unit", "collab"] as const;
export type OgsmScope = (typeof OGSM_SCOPES)[number];

export const OGSM_STATUSES = ["draft", "active"] as const;
export type OgsmStatus = (typeof OGSM_STATUSES)[number];
export const OGSM_STATUS_LABELS: Record<OgsmStatus, string> = { draft: "Draft", active: "Active" };

export const KPI_STATUSES = ["on_track", "at_risk", "off_track", "no_data"] as const;
export type KpiStatus = (typeof KPI_STATUSES)[number];
export const KPI_STATUS_LABELS: Record<KpiStatus, string> = {
  on_track: "On track",
  at_risk: "At risk",
  off_track: "Off track",
  no_data: "No data",
};
/** Counts per status (the objective's summary, a directie's row, a cascade node). */
export type KpiStatusCounts = Record<KpiStatus, number>;

export const KPI_DIRECTIONS = ["up", "down"] as const;
export type KpiDirection = (typeof KPI_DIRECTIONS)[number];
export const KPI_DIRECTION_LABELS: Record<KpiDirection, string> = { up: "Higher is better", down: "Lower is better" };

export const KPI_AGGREGATIONS = ["sum", "average", "weighted", "min", "max"] as const;
export type KpiAggregation = (typeof KPI_AGGREGATIONS)[number];
export const KPI_AGGREGATION_LABELS: Record<KpiAggregation, string> = {
  sum: "Sum",
  average: "Average",
  weighted: "Average weighted by days",
  min: "Lowest",
  max: "Highest",
};

export const OGSM_ACTION_STATUSES = ["open", "done", "dropped"] as const;
export type OgsmActionStatus = (typeof OGSM_ACTION_STATUSES)[number];
export const OGSM_ACTION_STATUS_LABELS: Record<OgsmActionStatus, string> = { open: "Open", done: "Done", dropped: "Dropped" };

/** Suggested KPI units (free text is allowed too, up to 20 characters). */
export const KPI_UNIT_SUGGESTIONS = ["%", "number", "€", "days"] as const;

// ---------- KPI sources ----------

export const KPI_SOURCE_MANUAL = "manual" as const;
export const KPI_SOURCE_CHILDREN = "children" as const;

/** The automatic sources: calculated per quarter from what Convenor already knows. */
export const KPI_AUTO_SOURCES = [
  { key: "collab.days", scopes: ["collab"], label: "Days used in the quarter", unit: "days" },
  { key: "collab.days_budget_pct", scopes: ["collab"], label: "Days used against the days budget", unit: "%" },
  { key: "collab.cost", scopes: ["collab"], label: "Cost in the quarter (personnel and other)", unit: "€" },
  { key: "collab.cost_budget_pct", scopes: ["collab"], label: "Cost against the budget", unit: "%" },
  { key: "collab.people", scopes: ["collab"], label: "People working on it", unit: "number" },
  { key: "collab.departments", scopes: ["collab"], label: "Directies the people come from", unit: "number" },
  { key: "collab.confirmed_pct", scopes: ["collab"], label: "Share of the quarter's lines confirmed", unit: "%" },
  { key: "unit.headcount", scopes: ["unit", "org"], label: "Headcount (the unit and below)", unit: "number" },
  { key: "unit.avg_workload", scopes: ["unit", "org"], label: "Average workload (5 people or more)", unit: "%" },
  { key: "unit.overloaded_pct", scopes: ["unit", "org"], label: "Share of people overloaded (5 people or more)", unit: "%" },
  { key: "unit.not_received_pct", scopes: ["unit", "org"], label: "Share of statements not received", unit: "%" },
  { key: "unit.collab_days_pct", scopes: ["unit", "org"], label: "Share of days on collaborations outside its own caseloads", unit: "%" },
] as const satisfies readonly { key: string; scopes: readonly OgsmScope[]; label: string; unit: string }[];
export type KpiAutoSourceKey = (typeof KPI_AUTO_SOURCES)[number]["key"];
export type KpiAutoSource = (typeof KPI_AUTO_SOURCES)[number];
/** 'manual' (readings entered per quarter), an automatic key, or 'children' (aggregate of the KPIs contributing to it). */
export type KpiSource = typeof KPI_SOURCE_MANUAL | typeof KPI_SOURCE_CHILDREN | KpiAutoSourceKey;
export const KPI_SOURCE_KEYS: readonly KpiSource[] = [KPI_SOURCE_MANUAL, KPI_SOURCE_CHILDREN, ...KPI_AUTO_SOURCES.map((s) => s.key)];

export function isKpiSource(s: string): s is KpiSource {
  return (KPI_SOURCE_KEYS as readonly string[]).includes(s);
}
/** The automatic sources valid for an OGSM of this scope. */
export function kpiAutoSourcesFor(scope: OgsmScope): KpiAutoSource[] {
  return KPI_AUTO_SOURCES.filter((s) => (s.scopes as readonly OgsmScope[]).includes(scope));
}
/** Whether a source may be used on an OGSM of this scope (manual and children everywhere). */
export function isKpiSourceValidFor(source: string, scope: OgsmScope): boolean {
  if (source === KPI_SOURCE_MANUAL || source === KPI_SOURCE_CHILDREN) return true;
  return kpiAutoSourcesFor(scope).some((s) => s.key === source);
}
/** The label of a source in a picker: manual, children or the automatic source's label. */
export function kpiSourceLabel(source: KpiSource): string {
  if (source === KPI_SOURCE_MANUAL) return "Entered each quarter";
  if (source === KPI_SOURCE_CHILDREN) return "Adds up the contributing KPIs";
  return KPI_AUTO_SOURCES.find((s) => s.key === source)?.label ?? source;
}

/** The automatic sources that are a unit's workload: values for owners/admins and the unit's managers (and above) only; null below 5 submitted statements. */
export const WORKLOAD_SOURCES: readonly KpiAutoSourceKey[] = ["unit.avg_workload", "unit.overloaded_pct", "unit.not_received_pct", "unit.collab_days_pct"];
/** The automatic sources worked out from the quarter's statements: no value while that quarter's round is still open. */
export const STATEMENT_SOURCES: readonly KpiAutoSourceKey[] = [
  "collab.days",
  "collab.days_budget_pct",
  "collab.cost",
  "collab.cost_budget_pct",
  "collab.people",
  "collab.departments",
  "collab.confirmed_pct",
  ...WORKLOAD_SOURCES,
];
export const isWorkloadSource = (source: string): boolean => (WORKLOAD_SOURCES as readonly string[]).includes(source);
export const isStatementSource = (source: string): boolean => (STATEMENT_SOURCES as readonly string[]).includes(source);

/**
 * Whether a KPI is money by its own source: the automatic 'collab.cost'. A
 * 'children' KPI is money too when anything it adds up is; the API works that
 * out over the contribution graph (the free-text unit does not count).
 */
export function isMoneySource(source: string): boolean {
  return source === "collab.cost";
}

// ---------- shared shapes ----------

/** A quarter: "Q3 2026". */
export type QuarterRef = { year: number; quarter: number };

/** Whose OGSM: the organisation, a unit or a collab. */
export type OgsmScopeRef = {
  scope: OgsmScope;
  /** The unit's or collab's id; null for the organisation. */
  id: Id | null;
  /** The organisation's, unit's or collab's name. */
  name: string;
  /** The unit kind ("Afdeling") or collab kind ("Programma"); "Organisation" for the organisation. */
  kindName: string;
  /** The top-level unit (directie) it belongs to: the unit's own (itself when it is one), a collab's sponsoring unit's. Null for the organisation. */
  departmentId: Id | null;
  departmentName: string | null;
};

/** A goal as a link target or source (contributions in and out). */
export type OgsmGoalLink = {
  goalId: Id;
  code: string;
  /** The code with its scope's prefix: "DU-G1" (see goalDisplayCode). */
  displayCode: string;
  text: string;
  ogsmId: Id;
  scope: OgsmScopeRef;
  status: KpiStatus;
};

/** A KPI as a link target or source. */
export type KpiLink = {
  kpiId: Id;
  name: string;
  unit: string;
  ogsmId: Id;
  scope: OgsmScopeRef;
};

/** A KPI in a summary line: name, value against target, traffic light. */
export type KpiBrief = {
  kpiId: Id;
  name: string;
  unit: string;
  value: number | null;
  target: number | null;
  status: KpiStatus;
  // What the path bar needs (ui/PathBar), the same as on the full Kpi:
  /** Null when not set, or for a money KPI the caller may not see. */
  baseline: number | null;
  /** On the path from baseline to target by now; null without a target or when hidden. */
  expected: number | null;
  direction: KpiDirection;
  /** There is a value, but not for this caller (money or workload). */
  hidden: boolean;
  baselineDate: string | null;
  targetDate: string | null;
  /** Decimals to show: 0 for count units (kpiDecimals). */
  decimals: number;
  /** "DU-G1.2" (kpiDisplayCode). */
  displayCode: string;
};

/**
 * Days in the quarter and cost in cents (cents null: money is for owners and
 * admins only). In `direct`, days and cents are both null when a unit's own
 * effort rests on fewer than 5 submitted statements; it is then left out of
 * the totals.
 */
export type EffortFigures = { days: number | null; cents: number | null };
/** Effort on a goal: its own share, plus what contributing goals pass up. */
export type GoalEffort = { direct: EffortFigures; viaContributions: EffortFigures; total: EffortFigures };

// ---------- the OGSM: GET/POST/PATCH /api/ogsm ----------

/** A KPI's value and status for one quarter. */
export type KpiValue = QuarterRef & {
  /** The value used: the quarter's own, or the latest earlier quarter that has one (valueFrom says which). */
  value: number | null;
  valueFrom: QuarterRef | null;
  /** The value came from an earlier quarter (valueFrom); the status compares it with the path at that quarter's end. */
  stale: boolean;
  /** On the straight path from baseline to target at the end of the quarter the value is from (valueFrom, else this quarter); null without a target. */
  expected: number | null;
  status: KpiStatus;
  /** There is a value, but not for this caller (money or workload): value and expected are null, the status stays. */
  hidden: boolean;
  /** A source worked out from statements, and this quarter's workload round is still open: no value for it yet. */
  quarterOpen: boolean;
  /** source 'children': how many contributing KPIs there are, and how many had no value (left out). */
  childCount: number | null;
  missingChildren: number | null;
};

export type Kpi = {
  id: Id;
  ogsmId: Id;
  /** Exactly one of goalId and strategyId is set. */
  goalId: Id | null;
  strategyId: Id | null;
  name: string;
  unit: string;
  direction: KpiDirection;
  baseline: number | null;
  baselineDate: string | null;
  target: number | null;
  targetDate: string | null;
  tolerancePct: number;
  source: KpiSource;
  /** Used only when source = 'children'. */
  aggregation: KpiAggregation;
  contributesToKpiId: Id | null;
  contributesTo: KpiLink | null;
  /** The KPIs whose contributesToKpiId is this one. */
  contributionsIn: number;
  position: number;
  /** For the quarter asked for (GET /ogsm?quarter=, default: see "Default quarter"). */
  current: KpiValue;
  /** Decimals to show: 0 for count units ("aantal", "number", "#", none), else 1 (kpiDecimals). */
  decimals: number;
  /** "DU-G1.2": its goal's or strategy's display code and its place under it (kpiDisplayCode). */
  displayCode: string;
};

/** PATCH /api/ogsm/:ogsmId/kpis/:kpiId: the KPI, and whether its "contributes to" link was cleared because the KPI moved to where that link is not allowed. */
export type KpiPatchResult = Kpi & { linkCleared: boolean };

export type OgsmGoal = {
  id: Id;
  ogsmId: Id;
  code: string;
  /** "DU-G1": the code with its scope's prefix (goalDisplayCode). */
  displayCode: string;
  text: string;
  position: number;
  /** Shares out effort over the goals (equal by default: 1). */
  weight: number;
  contributesToGoalId: Id | null;
  contributesTo: OgsmGoalLink | null;
  /** Goals (of lower units and collabs) that contribute to this one. */
  contributionsIn: OgsmGoalLink[];
  /** The strategies that serve it. */
  strategyIds: Id[];
  /** Worst of its KPIs, ignoring 'no_data'. */
  status: KpiStatus;
  kpis: Kpi[];
};

export type OgsmStrategy = {
  id: Id;
  ogsmId: Id;
  code: string;
  /** "DU-S1": the code with its scope's prefix (goalDisplayCode). */
  displayCode: string;
  text: string;
  position: number;
  goalIds: Id[];
  /** Worst of its KPIs, ignoring 'no_data'. */
  status: KpiStatus;
  kpis: Kpi[];
};

export type OgsmAction = {
  id: Id;
  ogsmId: Id;
  strategyId: Id | null;
  text: string;
  ownerEmployeeId: Id | null;
  ownerName: string | null;
  dueOn: string | null;
  status: OgsmActionStatus;
  doneOn: string | null;
  position: number;
  /** Open and past dueOn (calculated). */
  overdue: boolean;
};

export type Ogsm = {
  id: Id;
  scope: OgsmScopeRef;
  year: number;
  objective: string;
  status: OgsmStatus;
  /** The quarter the values and statuses are for. */
  quarter: QuarterRef;
  goals: OgsmGoal[];
  strategies: OgsmStrategy[];
  actions: OgsmAction[];
  /** KPI counts per status across the whole OGSM (the objective's summary). */
  statusCounts: KpiStatusCounts;
  canEdit: boolean;
  /** The same scope's OGSM the year before / after, when there is one. */
  previousYearId: Id | null;
  nextYearId: Id | null;
  createdAt: string;
  updatedAt: string;
};

const year = z.coerce.number().int().min(2000).max(2100);
const quarter = z.coerce.number().int().min(1, "Quarter 1-4.").max(4, "Quarter 1-4.");
const isoDateOrNull = z
  .union([isoDate, z.literal("")])
  .nullable()
  .transform((v) => (v === "" ? null : v));
/** A KPI number: finite, at most two decimals, within numeric(14,2). */
export const kpiNumber = z
  .number({ invalid_type_error: "Enter a number." })
  .finite("Enter a number.")
  .min(-999_999_999_999, "The number is too large.")
  .max(999_999_999_999, "The number is too large.")
  .refine((n) => Math.abs(Math.round(n * 100) - n * 100) < 1e-6, "Use at most two decimals.");
const kpiNumberOrNull = kpiNumber.nullable();
const position = z.number().int().min(0).max(10_000);

/** GET /api/ogsm?scope=&id=&year=&quarter= ; id is required for unit and collab, absent for org. Default year: this year (Brussels date); default quarter: see "Default quarter" above. */
export const ogsmQuery = z
  .object({ scope: z.enum(OGSM_SCOPES), id: uuid.optional(), year: year.optional(), quarter: quarter.optional() })
  .refine((v) => (v.scope === "org") === (v.id === undefined), { message: "Give an id for a unit or a collaboration, and none for the organisation.", path: ["id"] });
export type OgsmQuery = z.infer<typeof ogsmQuery>;

/** GET /api/ogsm/can-create?scope=&id=&year= : whether the caller may create (and edit) that scope's OGSM. Default year: this year. */
export const ogsmCanCreateQuery = z
  .object({ scope: z.enum(OGSM_SCOPES), id: uuid.optional(), year: year.optional() })
  .refine((v) => (v.scope === "org") === (v.id === undefined), { message: "Give an id for a unit or a collaboration, and none for the organisation.", path: ["id"] });
export type OgsmCanCreateQuery = z.infer<typeof ogsmCanCreateQuery>;
export type OgsmCanCreate = { canCreate: boolean };

/** POST /api/ogsm: a new (draft) OGSM; 409 when the scope has one for that year. */
export const ogsmCreateBody = z
  .object({
    scope: z.enum(OGSM_SCOPES),
    /** The unit or collab; null for the organisation. */
    id: uuid.nullable(),
    year,
    objective: textOrEmpty(2000, "Objective").default(""),
  })
  .refine((v) => (v.scope === "org") === (v.id === null), { message: "Give an id for a unit or a collaboration, and none for the organisation.", path: ["id"] });
export type OgsmCreateBody = z.infer<typeof ogsmCreateBody>;

/** PATCH /api/ogsm/:ogsmId */
export const ogsmPatchBody = z
  .object({ objective: textOrEmpty(2000, "Objective"), status: z.enum(OGSM_STATUSES) })
  .partial()
  .refine((v) => Object.keys(v).length > 0, "Nothing to change.");
export type OgsmPatchBody = z.infer<typeof ogsmPatchBody>;
export const ogsmParams = z.object({ ogsmId: uuid });
/** ?quarter= on GET-like calls of one OGSM (the one-pager PDF). */
export const ogsmQuarterQuery = z.object({ quarter: quarter.optional() });
// POST /api/ogsm/:ogsmId/copy-to-next-year (no body) returns the new Ogsm; 409 when next year's exists.

// ---------- goals: /api/ogsm/:ogsmId/goals[/:goalId] ----------

export const ogsmGoalBody = z.object({
  /** "G1"; the API fills the next free code when empty. */
  code: textOrEmpty(10, "Code").default(""),
  text: text(500, "Goal"),
  weight: z.number().int("A whole number.").min(0, "Cannot be negative.").max(100, "At most 100.").default(1),
  /** A goal from GET /goals/targets (400 when not allowed). */
  contributesToGoalId: uuid.nullable().default(null),
  /** Default: at the end. */
  position: position.optional(),
});
export type OgsmGoalBody = z.infer<typeof ogsmGoalBody>;
export const ogsmGoalPatch = z
  .object({
    code: textOrEmpty(10, "Code"),
    text: text(500, "Goal"),
    weight: z.number().int("A whole number.").min(0, "Cannot be negative.").max(100, "At most 100."),
    contributesToGoalId: uuid.nullable(),
    position,
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, "Nothing to change.");
export type OgsmGoalPatch = z.infer<typeof ogsmGoalPatch>;
export const ogsmGoalParams = z.object({ ogsmId: uuid, goalId: uuid });

// ---------- strategies: /api/ogsm/:ogsmId/strategies[/:strategyId] ----------

export const ogsmStrategyBody = z.object({
  code: textOrEmpty(10, "Code").default(""),
  text: text(500, "Strategy"),
  /** Goals of the same OGSM it serves (replaced as a whole). */
  goalIds: z.array(uuid).max(50).default([]),
  position: position.optional(),
});
export type OgsmStrategyBody = z.infer<typeof ogsmStrategyBody>;
export const ogsmStrategyPatch = z
  .object({ code: textOrEmpty(10, "Code"), text: text(500, "Strategy"), goalIds: z.array(uuid).max(50), position })
  .partial()
  .refine((v) => Object.keys(v).length > 0, "Nothing to change.");
export type OgsmStrategyPatch = z.infer<typeof ogsmStrategyPatch>;
export const ogsmStrategyParams = z.object({ ogsmId: uuid, strategyId: uuid });

// ---------- KPIs: /api/ogsm/:ogsmId/kpis[/:kpiId] ----------

const kpiSource = z.string().refine(isKpiSource, "Choose a source.");
const kpiFields = {
  name: text(200, "Name"),
  unit: text(20, "Unit"),
  direction: z.enum(KPI_DIRECTIONS),
  baseline: kpiNumberOrNull,
  baselineDate: isoDateOrNull,
  target: kpiNumberOrNull,
  targetDate: isoDateOrNull,
  tolerancePct: z.number().int("A whole number.").min(0, "Cannot be negative.").max(100, "At most 100."),
  /** Must be valid for the OGSM's scope (isKpiSourceValidFor; 400 otherwise). */
  source: kpiSource,
  aggregation: z.enum(KPI_AGGREGATIONS),
  /** A KPI from GET /kpis/targets (400 when not allowed). */
  contributesToKpiId: uuid.nullable(),
  position,
};
/** POST: under exactly one goal or one strategy of the same OGSM. */
export const kpiBody = z
  .object({
    goalId: uuid.nullable().default(null),
    strategyId: uuid.nullable().default(null),
    name: kpiFields.name,
    unit: kpiFields.unit.default("number"),
    direction: kpiFields.direction.default("up"),
    baseline: kpiFields.baseline.default(null),
    baselineDate: kpiFields.baselineDate.default(null),
    target: kpiFields.target.default(null),
    targetDate: kpiFields.targetDate.default(null),
    tolerancePct: kpiFields.tolerancePct.default(10),
    source: kpiFields.source.default(KPI_SOURCE_MANUAL),
    aggregation: kpiFields.aggregation.default("sum"),
    contributesToKpiId: kpiFields.contributesToKpiId.default(null),
    position: position.optional(),
  })
  .refine((v) => (v.goalId === null) !== (v.strategyId === null), { message: "Put the KPI under one goal or one strategy.", path: ["goalId"] })
  .refine((v) => !v.baselineDate || !v.targetDate || v.targetDate > v.baselineDate, { message: "The target date must be after the baseline date.", path: ["targetDate"] });
export type KpiBody = z.infer<typeof kpiBody>;
/** PATCH: setting goalId clears strategyId and the other way round; never both. */
export const kpiPatch = z
  .object({ goalId: uuid.nullable(), strategyId: uuid.nullable(), ...kpiFields })
  .partial()
  .refine((v) => Object.keys(v).length > 0, "Nothing to change.")
  .refine((v) => !(v.goalId && v.strategyId), { message: "Put the KPI under one goal or one strategy.", path: ["goalId"] })
  .refine((v) => !(v.goalId === null && v.strategyId === null), { message: "Put the KPI under one goal or one strategy.", path: ["goalId"] })
  .refine((v) => !v.baselineDate || !v.targetDate || v.targetDate > v.baselineDate, { message: "The target date must be after the baseline date.", path: ["targetDate"] });
export type KpiPatch = z.infer<typeof kpiPatch>;
export const kpiParamsInOgsm = z.object({ ogsmId: uuid, kpiId: uuid });
export const kpiParams = z.object({ kpiId: uuid });

// ---------- readings: PUT/DELETE /api/kpis/:kpiId/readings/:year/:quarter ----------

export type KpiReading = QuarterRef & {
  id: Id;
  kpiId: Id;
  value: number;
  note: string;
  enteredByUserId: Id | null;
  /** The account's name; null when unknown. */
  enteredByName: string | null;
  enteredAt: string;
};
/** PUT: manual KPIs only (409 for automatic and children KPIs). */
export const kpiReadingBody = z.object({ value: kpiNumber, note: textOrEmpty(2000, "Note").default("") });
export type KpiReadingBody = z.infer<typeof kpiReadingBody>;
export const kpiReadingParams = z.object({ kpiId: uuid, year, quarter });
/** ?as=<employeeId> on PUT/DELETE readings: an owner/admin (act-as enabled) enters it for that employee; the edit check uses the employee's rights, enteredByUserId stays the signed-in account. */
export const kpiReadingQuery = z.object({ as: uuid.optional() });

// ---------- actions: /api/ogsm/:ogsmId/actions[/:actionId] ----------

export const ogsmActionBody = z.object({
  text: text(500, "Action"),
  /** A strategy of the same OGSM, or null. */
  strategyId: uuid.nullable().default(null),
  ownerEmployeeId: uuid.nullable().default(null),
  dueOn: isoDateOrNull.default(null),
  status: z.enum(OGSM_ACTION_STATUSES).default("open"),
  /** Default: today when status becomes 'done'. */
  doneOn: isoDateOrNull.optional(),
  position: position.optional(),
});
export type OgsmActionBody = z.infer<typeof ogsmActionBody>;
export const ogsmActionPatch = z
  .object({
    text: text(500, "Action"),
    strategyId: uuid.nullable(),
    ownerEmployeeId: uuid.nullable(),
    dueOn: isoDateOrNull,
    status: z.enum(OGSM_ACTION_STATUSES),
    doneOn: isoDateOrNull,
    position,
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, "Nothing to change.");
export type OgsmActionPatch = z.infer<typeof ogsmActionPatch>;
export const ogsmActionParams = z.object({ ogsmId: uuid, actionId: uuid });

// ---------- link targets: GET /api/goals/targets, /api/kpis/targets ----------

/** GET /api/goals/targets?ogsmId= : goals this OGSM's goals may contribute to (same year; a unit: ancestor units and the organisation; a collab: any unit or the organisation). */
export const goalTargetsQuery = z.object({ ogsmId: uuid });
export type GoalOption = OgsmGoalLink & {
  year: number;
  /** 0 = the organisation, 1 = a directie, and so on (for indenting a picker). */
  depth: number;
};

/** GET /api/kpis/targets?kpiId= : KPIs this KPI may contribute to (in an OGSM whose goal chain its goal contributes to, or higher; no cycles), in OGSMs the caller may edit too. */
export const kpiTargetsQuery = z.object({ kpiId: uuid });
export type KpiOption = KpiLink & {
  goalId: Id | null;
  goalCode: string | null;
  source: KpiSource;
  aggregation: KpiAggregation;
  depth: number;
};

// ---------- KPI page: GET /api/kpis/:kpiId?year= ----------

export const kpiDetailQuery = z.object({ year: year.optional() });
export type KpiDetailQuery = z.infer<typeof kpiDetailQuery>;

/** One quarter on the KPI's chart. */
export type KpiPoint = KpiValue & {
  /** 'reading' (manual, entered), 'calculated' (automatic or children), null when there is no value. */
  origin: "reading" | "calculated" | null;
};

/** A KPI contributing to this one, for the chosen quarter. */
export type KpiContribution = KpiLink & {
  value: number | null;
  status: KpiStatus;
  /** For 'sum': its value / the total (0-1); null otherwise, without values, or when money or workload hides the figures. */
  share: number | null;
  /** For 'weighted': the child scope's days in the quarter; null otherwise, below 5 submitted statements, for a unit whose days the caller may not see, or for a hidden money KPI. */
  weightDays: number | null;
};

export type KpiDetail = {
  kpi: Kpi;
  scope: OgsmScopeRef;
  /** The year of the series (?year=, default the KPI's OGSM's year). */
  year: number;
  /** The year of the KPI's OGSM: readings are entered for its quarters only. */
  ogsmYear: number;
  goal: { id: Id; code: string; text: string } | null;
  strategy: { id: Id; code: string; text: string } | null;
  /** Q1-Q4 of the year (quarters after today included with value null). */
  series: KpiPoint[];
  /** The quarter of kpi.current and the contributions: the year's default quarter (see "Default quarter"). */
  contributionsQuarter: QuarterRef;
  contributions: KpiContribution[];
  /** Manual KPIs: all readings, newest first; [] otherwise. */
  readings: KpiReading[];
  canEdit: boolean;
};

// ---------- cascade: GET /api/ogsm/cascade?year=&quarter=&rootGoalId= ----------

/** Default year: this year (Brussels date); default quarter: see "Default quarter" above. */
export const cascadeQuery = z.object({ year: year.optional(), quarter: quarter.optional(), rootGoalId: uuid.optional() });
export type CascadeQuery = z.infer<typeof cascadeQuery>;

export type CascadeNode = {
  goalId: Id;
  code: string;
  /** "DU-G1": the code prefixed with the scope's short code (goalDisplayCode). */
  displayCode: string;
  text: string;
  ogsmId: Id;
  scope: OgsmScopeRef;
  /** A collaboration's goal (shown with a distinct marker). */
  isCollab: boolean;
  status: KpiStatus;
  kpiCounts: KpiStatusCounts;
  kpis: KpiBrief[];
  effort: GoalEffort;
  /** The goals contributing to this one. */
  children: CascadeNode[];
};

export type Cascade = QuarterRef & {
  /** The organisation's goals, or the one goal asked for (rootGoalId). */
  roots: CascadeNode[];
  /** The directies, for the filter. */
  departments: { id: Id; name: string }[];
  /** Goals in this year's OGSMs that contribute to nothing and are not the organisation's (not in the tree), by scope then position. */
  unlinkedGoals: UnlinkedGoal[];
};

/** A goal outside the cascade: it contributes to no higher goal yet. */
export type UnlinkedGoal = { goalId: Id; code: string; /** "DU-G1" (goalDisplayCode). */ displayCode: string; text: string; ogsmId: Id; scope: OgsmScopeRef };

// ---------- to report: GET /api/ogsm/to-report?year=&quarter=&as= ----------

/**
 * Default: the current quarter. as=<employeeId>: act as that employee
 * (owner/admin, act-as enabled). An owner or admin who is not acting gets the
 * KPIs where they are the natural editor (the organisation's OGSM, the OGSMs
 * of units they manage and of collabs they own); all=1 gives every OGSM.
 */
export const toReportQuery = z.object({ year: year.optional(), quarter: quarter.optional(), as: uuid.optional(), all: z.enum(["0", "1"]).optional() });
export type ToReportQuery = z.infer<typeof toReportQuery>;

/** A manual KPI the caller edits that has no reading for the quarter. */
export type ReportItem = QuarterRef & {
  kpiId: Id;
  name: string;
  unit: string;
  direction: KpiDirection;
  target: number | null;
  targetDate: string | null;
  ogsmId: Id;
  scope: OgsmScopeRef;
  /** displayCode: "DSS-G2" (goalDisplayCode). */
  goal: { id: Id; code: string; displayCode: string; text: string } | null;
  strategy: { id: Id; code: string; displayCode: string; text: string } | null;
  /** The most recent reading before this quarter, if any. */
  lastReading: (QuarterRef & { value: number }) | null;
  /** The due date of the open workload round (readings come in with it), or null when no round is open. */
  dueOn: string | null;
  /** For the path bar: the KPI's baseline, and the value expected at the end of the quarter to report (null without a target). */
  baseline: number | null;
  expected: number | null;
  /** The at-risk band in % of |target - baseline| (the status rule). */
  tolerancePct: number;
};

// ---------- analytics: GET /api/analytics/overview?year=&quarter= ----------

export const ogsmAnalyticsQuery = z.object({ year: year.optional(), quarter: quarter.optional() });
export type OgsmAnalyticsQuery = z.infer<typeof ogsmAnalyticsQuery>;
/** GET /api/analytics/export.xlsx?year= (org-admin). */
export const ogsmAnalyticsExportQuery = z.object({ year: year.optional() });

export type AnalyticsGoalTile = {
  goalId: Id;
  code: string;
  /** "ORG-G1" (goalDisplayCode). */
  displayCode: string;
  text: string;
  status: KpiStatus;
  kpis: KpiBrief[];
  effort: GoalEffort;
};

export type AnalyticsDepartmentRow = {
  /** Null: the organisation's own OGSM. */
  departmentId: Id | null;
  name: string;
  counts: KpiStatusCounts;
};

export type AnalyticsOffTrackKpi = KpiBrief & {
  expected: number | null;
  ogsmId: Id;
  scope: OgsmScopeRef;
  /** How far behind the path, as a share of |target − baseline| (larger = worse); the list is sorted by it, KPIs without one (hidden figures) last. */
  gap: number | null;
  /** How far behind the path in the KPI's own unit ("25 days", "12 points"); null when the figures are hidden. */
  gapValue: number | null;
  /** gapValue / |target − baseline| (|target| without a baseline; 999 when target equals baseline): the list's order, worst first. Null when the figures are hidden. */
  relativeGap: number | null;
};

export type AnalyticsOverdueAction = {
  actionId: Id;
  text: string;
  ogsmId: Id;
  scope: OgsmScopeRef;
  ownerName: string | null;
  dueOn: string;
  daysOverdue: number;
};

export type AnalyticsPortfolioItem = {
  collabId: Id;
  name: string;
  kindName: string;
  /** Null: no OGSM this year. */
  ogsmId: Id | null;
  /** The worst status of any KPI in the collab's plan for the quarter (goals and strategies alike), the same rule as the off-track list. */
  status: KpiStatus;
  /** No KPI of its plan has a figure for the quarter (or it has no plan): listed under the chart, not drawn. */
  noData: boolean;
  days: number;
  budgetDays: number | null;
  /** days / budget × 100; null without a budget. */
  daysPct: number | null;
  /** Null for callers who are not owner/admin (money). */
  cents: number | null;
  /** Null without a budget, or for callers who are not owner/admin. */
  budgetCents: number | null;
  /** cost / budget × 100; null without a budget (shown to every member). */
  costPct: number | null;
  people: number;
  departments: number;
};

export type AnalyticsCoverage = {
  unitsTotal: number;
  unitsCovered: number;
  collabsTotal: number;
  collabsCovered: number;
  /** Units and collabs without an active OGSM this year. */
  unitsWithout: { unitId: Id; name: string; kindName: string; departmentName: string | null }[];
  collabsWithout: { collabId: Id; name: string; kindName: string }[];
};

export type AnalyticsOverview = QuarterRef & {
  /** The organisation's OGSM this year; null when there is none. */
  orgOgsmId: Id | null;
  goals: AnalyticsGoalTile[];
  totals: KpiStatusCounts;
  byDepartment: AnalyticsDepartmentRow[];
  offTrack: AnalyticsOffTrackKpi[];
  overdueActions: AnalyticsOverdueAction[];
  portfolio: AnalyticsPortfolioItem[];
  coverage: AnalyticsCoverage;
};

// ---------- display codes and decimals (path bar, one-pager) ----------

/** KPI units counted in whole numbers (compared trimmed, any case). */
export const KPI_COUNT_UNITS = ["aantal", "number", "#", ""] as const;

/** A count unit: whole numbers, and the unit is not written after the figure. */
export const isKpiCountUnit = (unit: string): boolean => (KPI_COUNT_UNITS as readonly string[]).includes(unit.trim().toLowerCase());

/** KPI units that are euro amounts (compared trimmed, any case): shown in whole euros. */
export const KPI_MONEY_UNITS = ["€", "eur", "euro", "euros"] as const;

/** Decimals to show for a KPI's figures: 0 for count units and money (a euro unit, or a money source such as 'collab.cost'), else 1. */
export function kpiDecimals(unit: string, source?: string): number {
  const u = unit.trim().toLowerCase();
  if (isKpiCountUnit(u) || (KPI_MONEY_UNITS as readonly string[]).includes(u)) return 0;
  return source !== undefined && isMoneySource(source) ? 0 : 1;
}

/** Small words left out of initials ("Bedrijfsvoering en ICT" -> BI). */
const INITIALS_SKIP = new Set(["en", "and", "of", "van", "de", "der", "den", "het", "the", "voor", "for", "te", "ter", "'t", "&"]);

/** The initials of a name, upper case: "Directie Uitvoering" -> "DU". At most 5 letters; "" for a name without letters. */
export function initialsOf(name: string): string {
  return name
    .split(/[\s\-_/]+/)
    .filter((w) => w && !INITIALS_SKIP.has(w.toLowerCase()))
    .map((w) => /[\p{L}\p{N}]/u.exec(w)?.[0] ?? "")
    .join("")
    .slice(0, 5)
    .toUpperCase();
}

/**
 * The prefix of a scope's codes: "ORG" for the organisation; a unit's short
 * name, else the initials of its name; a collab's code, else the initials of
 * its name.
 */
export function scopeCodePrefix(s: { scope: OgsmScope; shortName?: string | null; code?: string | null; name: string }): string {
  if (s.scope === "org") return "ORG";
  const own = (s.scope === "unit" ? s.shortName : s.code)?.trim();
  if (own) return own;
  return initialsOf(s.name) || (s.scope === "unit" ? "U" : "C");
}

/** "DU-G1" from the prefix and the goal's (or strategy's) own code; the prefix alone when the code is empty. */
export function goalDisplayCode(prefix: string, code: string): string {
  const c = code.trim();
  return c ? `${prefix}-${c}` : prefix;
}

/** "DU-G1.2": the KPI's goal's or strategy's display code and its place (1-based) under it. */
export function kpiDisplayCode(parentDisplayCode: string, ordinal: number): string {
  return `${parentDisplayCode}.${ordinal}`;
}

// ---------- form inputs (before defaults) ----------

export type OgsmCreateInput = z.input<typeof ogsmCreateBody>;
export type OgsmPatchInput = z.input<typeof ogsmPatchBody>;
export type OgsmGoalInput = z.input<typeof ogsmGoalBody>;
export type OgsmGoalPatchInput = z.input<typeof ogsmGoalPatch>;
export type OgsmStrategyInput = z.input<typeof ogsmStrategyBody>;
export type OgsmStrategyPatchInput = z.input<typeof ogsmStrategyPatch>;
export type KpiInput = z.input<typeof kpiBody>;
export type KpiPatchInput = z.input<typeof kpiPatch>;
export type KpiReadingInput = z.input<typeof kpiReadingBody>;
export type OgsmActionInput = z.input<typeof ogsmActionBody>;
export type OgsmActionPatchInput = z.input<typeof ogsmActionPatch>;

// ---------- To report: save many readings at once (UX consolidation, My tasks) ----------

/** PUT /api/kpi-readings?as= : many readings in one go, all or nothing; the same rights per item as PUT /kpis/:kpiId/readings/:year/:quarter. */
export const kpiReadingsBatchBody = z.object({
  items: z
    .array(z.object({ kpiId: uuid, year, quarter, value: kpiNumber, note: textOrEmpty(2000, "Note").default("") }))
    .min(1, "Enter at least one value.")
    .max(200, "At most 200 readings at once."),
});
export type KpiReadingsBatchBody = z.infer<typeof kpiReadingsBatchBody>;
export type KpiReadingsBatchInput = z.input<typeof kpiReadingsBatchBody>;
/** What PUT /api/kpi-readings returns. */
export type KpiReadingsBatchResult = { saved: number };
